import { AxiosInstance } from "axios";
import { RELATIONSHIP_HIERARCHY, unstrapiMany } from "../helpers/strapi";
import { BaseContentType } from "./types";

interface StrapiPaginationMeta {
  pagination: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
  };
}

interface StrapiResponse<T> {
  data: T[];
  meta: StrapiPaginationMeta;
}

/**
 * Fetches all records from a Strapi API endpoint by handling pagination automatically
 * @param apiClient - Axios instance configured for the API
 * @param endpoint - The API endpoint to fetch from
 * @param params - Additional query parameters to include in the request
 * @param pageSize - Number of items per page (default: 500)
 * @returns Promise with array of all records
 */
export async function fetchAllStrapiRecords<T extends BaseContentType>(
  apiClient: AxiosInstance,
  endpoint: string,
  params: Record<string, any> = {},
  strapiEntity: keyof typeof RELATIONSHIP_HIERARCHY,
  pageSize: number = 500
): Promise<T[]> {
  const allRecords: T[] = [];
  const finalMeta: StrapiPaginationMeta = {
    pagination: {
      page: 1,
      pageSize: 1,
      pageCount: 1,
      total: 1
    }
  };

  let currentPage = 1;
  let hasMorePages = true;

  while (hasMorePages) {
    const response = await apiClient.get<StrapiResponse<T>>(endpoint, {
      params: {
        ...params,
        "pagination[page]": currentPage,
        "pagination[pageSize]": pageSize
      }
    });

    const { data, meta } = response.data;
    allRecords.push(...data);
    finalMeta.pagination.total = meta.pagination.total;
    finalMeta.pagination.pageCount = meta.pagination.pageCount;

    // Check if we've reached the last page
    hasMorePages = currentPage < meta.pagination.pageCount;
    currentPage++;
  }

  return unstrapiMany<T>({ data: allRecords } as any, strapiEntity);
}
